import Keycloak from 'keycloak-js'

/**
 * The Keycloak singleton.
 *
 * Development authenticates against PRODUCTION Keycloak with a real staff
 * account: there is no local Keycloak in this repo, and — more importantly —
 * there is no dev-auth bypass. See docs/DB_ACCESS.md for why. The short version:
 * the product's `crates/local-mode` decides locality by HOSTNAME and never reads
 * the port, so the SSM tunnel would make it report "local" while pointed at the
 * production database, and the product's bypass short-circuits the staff-row
 * check as well. What this app offers instead is fixture MODE, which removes the
 * need for DATA rather than the need to log in.
 *
 * Access tokens here live 900 s, not 86,400 — see the client config in the realm
 * PR. That is a 96x shorter window than the product's, which is why the
 * refresh-before-attach in `lib/api/internalClient.ts` matters more here.
 */
export const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
})

/**
 * `keycloak.init()`, callable many times, executed once.
 *
 * `Keycloak.init` THROWS on a second call ("A 'Keycloak' instance can only be
 * initialized once"), and `<StrictMode>` in development mounts every effect
 * twice on purpose. The result was not a visible error but a permanent lie:
 * mount 1 started the real init and then had its result discarded by its own
 * cleanup, mount 2 threw and the catch set `ready = true` with
 * `authenticated = false`. The app then believed nobody was logged in even when
 * Keycloak had just said otherwise.
 *
 * With a live Keycloak session that is an INFINITE REDIRECT LOOP — StaffOnly
 * sends you to Keycloak, Keycloak recognises the session and bounces you
 * straight back, the SPA boots and concludes again that you are anonymous. The
 * screen flickers white/dark once per round trip. Without a session it looks
 * fine, because the flow simply stops at the login form: the bug needs an
 * authenticated user to show itself, which is exactly the user who matters.
 *
 * Hoisting the promise to module scope makes both mounts await the SAME init,
 * so the second one observes the first one's answer instead of an exception.
 */
let initPromise: Promise<boolean> | null = null

export function initKeycloakOnce(): Promise<boolean> {
  initPromise ??= keycloak.init({
    onLoad: 'check-sso',
    pkceMethod: 'S256',
    checkLoginIframe: false,
    // ── NO silentCheckSsoRedirectUri, AND THAT IS THE POINT ──────────────────
    // keycloak-js skips its third-party-cookie probe only when BOTH the login
    // iframe is disabled AND no silent redirect URI is set:
    //
    //   if ((!loginIframe.enable && !silentCheckSsoRedirectUri) || …) return
    //
    // We had the first and not the second, so it still injected a hidden iframe
    // pointing at keycloak.fintela.io. Firefox's Total Cookie Protection blocks
    // that frame's storage, the frame never posts back, and after the 10-second
    // messageReceiveTimeout init() REJECTS with
    //
    //   Timeout when waiting for 3rd party check iframe message.
    //
    // The user sees a spinner for ten seconds and then a permanently blank app.
    //
    // Dropping it costs nothing here. `check-sso` without a silent URI does a
    // TOP-LEVEL redirect with prompt=none — no iframe, no third-party storage,
    // works in every browser — and <StaffOnly> redirects to login anyway when
    // the answer is "not authenticated". The silent variant only ever saved a
    // redirect we were going to perform regardless.
  })
  return initPromise
}

/** True when the SPA is running on sample data and must never call the network. */
export const FIXTURES_ENABLED =
  import.meta.env.DEV && import.meta.env.VITE_INTERNAL_FIXTURES === '1'
